import { Controller } from "@hotwired/stimulus"
import {
  createHereMap,
  disposeHereMap,
  circleIcon,
  singleBubble,
  fitToObjects,
} from "../helpers/here_map_helpers"

// Fleet-Karte: ein Marker pro Fahrzeug mit GPS-Position (123erfasst, GPS-Tracker wie PAJ,
// Telematik-API). Die Positionen kommen alle 15 Minuten aus den Integrationen; die Karte
// fragt mit refreshUrl jede refreshInterval ms nach und ersetzt die Marker, ohne den
// Ausschnitt zu verschieben. In einem verborgenen Tab wird nicht nachgefragt.
const GERMANY_CENTER = { lat: 51.1657, lng: 10.4515 }

// Keyed by fleets.telemetry_provider: 123erfasst stores the upstream tracker vendor, the
// tracking pipeline stores the provider key.
export const PROVIDER_COLORS = {
  CAT: "#fbbf24",
  Inseego: "#3b82f6",
  Trackunit: "#10b981",
  IRIS: "#a855f7",
  paj_gps: "#ef4444",
  yellowfox: "#f97316",
  api: "#0ea5e9",
}
const DEFAULT_COLOR = "#6b7280"

export default class extends Controller {
  static targets = ["map"]
  static values = {
    markers: Array,
    labels: Object,
    refreshUrl: String,
    refreshInterval: { type: Number, default: 60000 },
  }

  connect() {
    createHereMap(this.mapTarget, { center: this.computeCenter(), zoom: 6 })
      .then(({ H, map, ui }) => this.initMarkers(H, map, ui))
      .catch((error) => console.error("HERE map failed to load", error))
  }

  disconnect() {
    clearInterval(this.timer)
    this.timer = null
    disposeHereMap(this.map)
    this.map = null
  }

  initMarkers(H, map, ui) {
    this.H = H
    this.map = map
    this.showBubble = singleBubble(H, ui)
    this.group = new H.map.Group()
    map.addObject(this.group)
    const markers = this.renderMarkers(this.markersValue)

    if (markers.length > 1) fitToObjects(H, map, markers)
    if (this.hasRefreshUrlValue && this.refreshIntervalValue > 0) {
      this.timer = setInterval(() => this.refresh(), this.refreshIntervalValue)
    }
  }

  renderMarkers(markers) {
    this.group.removeAll()
    return markers.map((data) => {
      const marker = this.buildMarker(data)
      this.group.addObject(marker)
      return marker
    })
  }

  async refresh() {
    if (document.hidden || !this.map) return

    try {
      const response = await fetch(this.refreshUrlValue, {
        headers: { Accept: "application/json" },
      })
      if (!response.ok) return
      const { markers } = await response.json()
      if (this.map && Array.isArray(markers)) this.renderMarkers(markers)
    } catch (error) {
      // A missed refresh is harmless — the next tick tries again.
      console.warn("Fleet map refresh failed", error)
    }
  }

  buildMarker(data) {
    const H = this.H
    const position = { lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) }
    const color = PROVIDER_COLORS[data.telemetry_provider] || DEFAULT_COLOR

    const marker = new H.map.Marker(position, { icon: circleIcon(color) })
    marker.addEventListener("tap", () => this.showBubble(position, this.infoContent(data)))
    return marker
  }

  providerLabel(provider) {
    if (!provider) return "—"
    const names = this.labelsValue.providers || {}
    return names[provider] || provider
  }

  infoContent(data) {
    const labels = this.labelsValue
    const provider = this.escapeHtml(this.providerLabel(data.telemetry_provider))
    const updated = data.telemetry_updated_at
      ? this.escapeHtml(new Date(data.telemetry_updated_at).toLocaleString(labels.locale))
      : "—"
    const hours = data.operating_hours == null ? "—" : `${this.escapeHtml(data.operating_hours)} h`
    const km = data.odometer_km == null ? "—" : `${this.escapeHtml(data.odometer_km)} km`
    const subline = [data.number, data.license_number]
      .filter(Boolean)
      .map((part) => this.escapeHtml(part))
      .join(" · ")

    return `
      <div class="w-56 flex flex-col text-sm">
        <h3 class="font-semibold">${this.escapeHtml(data.name)}</h3>
        ${subline ? `<p class="text-gray-500">${subline}</p>` : ""}
        <p class="mt-1"><strong>${this.escapeHtml(labels.provider)}:</strong> ${provider}</p>
        <p><strong>${this.escapeHtml(labels.updated)}:</strong> ${updated}</p>
        <p><strong>${this.escapeHtml(labels.operating_hours)}:</strong> ${hours}</p>
        <p><strong>${this.escapeHtml(labels.odometer)}:</strong> ${km}</p>
        <a href="${encodeURI(data.url)}" data-turbo-frame="_top" class="text-bautask-green underline mt-2 self-end">
          ${this.escapeHtml(labels.show)}
        </a>
      </div>
    `
  }

  escapeHtml(value) {
    if (value == null) return ""
    const el = document.createElement("div")
    el.textContent = String(value)
    return el.innerHTML
  }

  computeCenter() {
    if (!this.markersValue.length) return GERMANY_CENTER
    const lats = this.markersValue.map((m) => parseFloat(m.latitude))
    const lngs = this.markersValue.map((m) => parseFloat(m.longitude))
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    }
  }
}
